import type { TrackMeters, TrackState } from '@melodinai/types';
import { SslMeter } from './meters/SslMeter';
import { useMixerStore } from '../../stores/mixerStore';
import { clsx } from '../../lib/utils';

interface ChannelFaderProps {
  track: TrackState;
  selected: boolean;
  meters?: TrackMeters;
  compact?: boolean;
  onSelect: () => void;
}

function linToDb(lin: number): string {
  if (lin <= 0.0001) return '-∞';
  return (20 * Math.log10(lin)).toFixed(1);
}

export function ChannelFader({ track, selected, meters, compact, onSelect }: ChannelFaderProps) {
  const mode = useMixerStore((s) => s.mode);
  const setVolume = useMixerStore((s) => s.setVolume);
  const setPan = useMixerStore((s) => s.setPan);
  const setMute = useMixerStore((s) => s.setMute);
  const setSolo = useMixerStore((s) => s.setSolo);
  const setArmed = useMixerStore((s) => s.setArmed);
  const meterH = compact ? 72 : 120;
  const isMaster = track.type === 'master';
  const sends = track.sends || [0, 0, 0, 0];

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onSelect(); }}
      className={clsx(
        'flex w-[76px] shrink-0 flex-col items-center gap-1 rounded border px-1.5 py-2',
        'bg-ssl-graphite text-ssl-text',
        selected ? 'border-ssl-accent shadow-[inset_0_0_0_1px_rgba(196,163,90,0.35)]' : 'border-ssl-metal/60',
        isMaster && 'bg-[#1e1810]',
      )}
    >
      <div className="h-1 w-full rounded-sm" style={{ background: track.color }} />
      <div className="w-full truncate text-center text-[10px] font-medium" title={track.name}>
        {track.name}
      </div>

      <div className="flex w-full items-end justify-center gap-1">
        <SslMeter
          peakL={meters?.peakL}
          peakR={meters?.peakR}
          rmsL={meters?.rmsL}
          rmsR={meters?.rmsR}
          grDb={meters?.grDb}
          height={meterH}
          showGr={!isMaster}
        />
        <input
          type="range"
          min={0}
          max={1.5}
          step={0.01}
          value={track.volume}
          aria-label={`${track.name} volume`}
          className="ssl-fader h-[120px] w-5 cursor-ns-resize appearance-none bg-transparent"
          style={{ writingMode: 'vertical-lr', direction: 'rtl', height: meterH }}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => setVolume(track.id, Number(e.target.value))}
        />
      </div>

      <div className="text-[9px] tabular-nums text-ssl-muted">{linToDb(track.volume)} dB</div>

      <input
        type="range"
        min={-1}
        max={1}
        step={0.01}
        value={track.pan}
        aria-label={`${track.name} pan`}
        className="h-1 w-full cursor-pointer accent-ssl-accent"
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => setPan(track.id, Number(e.target.value))}
      />

      <div className="flex gap-0.5">
        <button
          type="button"
          className={clsx(
            'h-5 w-5 rounded text-[9px] font-bold',
            track.muted ? 'bg-ssl-ledWarn text-black' : 'bg-ssl-rail text-ssl-muted',
          )}
          onClick={(e) => { e.stopPropagation(); setMute(track.id, !track.muted); }}
        >
          M
        </button>
        <button
          type="button"
          className={clsx(
            'h-5 w-5 rounded text-[9px] font-bold',
            track.soloed ? 'bg-yellow-400 text-black' : 'bg-ssl-rail text-ssl-muted',
          )}
          onClick={(e) => { e.stopPropagation(); setSolo(track.id, !track.soloed); }}
        >
          S
        </button>
        {!isMaster && track.type !== 'return' && (
          <button
            type="button"
            className={clsx(
              'h-5 w-5 rounded text-[9px] font-bold',
              track.armed ? 'bg-ssl-ledClip text-white' : 'bg-ssl-rail text-ssl-muted',
            )}
            onClick={(e) => { e.stopPropagation(); setArmed(track.id, !track.armed); }}
          >
            R
          </button>
        )}
      </div>

      {mode === 'studio' && !isMaster && track.type !== 'return' && (
        <div className="flex w-full justify-between gap-0.5 px-0.5">
          {sends.slice(0, 4).map((v, i) => (
            <span
              key={i}
              className={clsx(
                'h-1.5 flex-1 rounded-sm',
                v > 0.02 ? 'bg-ssl-led' : 'bg-ssl-ledDim',
              )}
              style={{ opacity: 0.35 + v * 0.65 }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
